import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, ChartBarIcon, EyeIcon, TrophyIcon } from "@heroicons/react/24/outline";
import { Container, EmptyState, Loading, MetricCard, PageHeader, RowAction, Select, Table, Tabs, Tag } from "../ui";
import { formatKigaliDate } from "../scholarships/model";
import { formatDateTime, formatScore } from "../scholarships/review/model";
import { ApplicantName } from "../scholarships/review/EligibilityFlag";
import { callPhaseOf, useAbroadCall } from "./model";
import { applicationStateOf, reviewApplicantsPath, useCallScores, useCommittee } from "./review";
import AwardListPanel from "./AwardListPanel";
import ScoredApplicationDialog from "./ScoredApplicationDialog";

const TABS = [
  { value: "applications", label: "Applications", icon: <ChartBarIcon className="h-4 w-4" /> },
  { value: "award", label: "Award list", icon: <TrophyIcon className="h-4 w-4" /> },
];

// Every submitted application with its marks so far; each committee member marks it unless they declared a conflict
function ApplicationsPanel({ call, rows, isPending, committeeSize, onOpen }) {
  const [programmeId, setProgrammeId] = useState("");
  const shown = programmeId ? rows.filter((r) => r.programmeId === programmeId) : rows;
  const programmes = call.programmes.map(({ programme: p }) => ({ value: p.id, label: p.name }));

  const columns = [
    { key: "applicant", label: "Applicant", accessor: (r) => r.applicantName, render: (r) => <ApplicantName name={r.applicantName} meets={r.meetsEligibility} /> },
    { key: "programme", label: "Programme", accessor: (r) => r.programmeName },
    { key: "submittedAt", label: "Submitted", accessor: (r) => formatDateTime(r.submittedAt), sortValue: (r) => r.submittedAt },
    { key: "marked", label: "Marked", accessor: (r) => `${r.evaluationsSubmitted} / ${Math.max(committeeSize - r.conflictsDeclared, r.evaluationsSubmitted)}` },
    { key: "evaluationsInDraft", label: "In progress" },
    { key: "conflictsDeclared", label: "Conflicts" },
    { key: "averageTotal", label: "Average score", accessor: (r) => formatScore(r.averageTotal), sortValue: (r) => r.averageTotal },
    { key: "state", label: "State", accessor: (r) => applicationStateOf(r.state).label, render: (r) => <Tag color={applicationStateOf(r.state).color}>{applicationStateOf(r.state).label}</Tag> },
    { key: "open", label: "", exportable: false, render: (r) => <RowAction Icon={EyeIcon} label="View" onClick={() => onOpen(r)} /> },
  ];

  return (
    <Table cardView viewKey="abroad-call-applications"
      columns={columns}
      data={shown}
      onRowClick={onOpen}
      isLoading={isPending}
      searchable
      exportName={`applications-${call.name}`}
      emptyTitle="No submitted applications yet"
      filters={
        programmes.length > 1 && (
          <Select aria-label="Programme" placeholder="All programmes" value={programmeId} onChange={(e) => setProgrammeId(e.target.value)} options={programmes} className="w-full sm:w-64" />
        )
      }
    />
  );
}

// One abroad loan call under review: its submitted applications and scores, then the award list
export default function CallReviewPage() {
  const { callId } = useParams();
  const [tab, setTab] = useState("applications");
  const [openRow, setOpenRow] = useState(null);
  const { data: call, isPending, isError, refetch } = useAbroadCall(callId);
  const { data: rows = [], isPending: rowsPending } = useCallScores(callId);
  const { data: members = [] } = useCommittee(callId);

  if (isPending) return <Loading label="Loading the loan call…" />;
  if (isError) return <EmptyState title="Could not load this loan call" description="It may have been removed, or you may not have access." onRefresh={refetch} />;

  const phase = callPhaseOf(call);
  const marksSubmitted = rows.reduce((sum, r) => sum + r.evaluationsSubmitted, 0);

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={reviewApplicantsPath} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> All loan calls
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {call.name}
            <Tag color={phase.color}>{phase.label}</Tag>
          </span>
        }
        subtitle={[call.academicYearName, `closes ${formatKigaliDate(call.closesAt)}`].filter(Boolean).join(" · ")}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard title="Submitted" value={rows.length} />
        <MetricCard title="Committee" value={members.length} />
        <MetricCard title="Marks submitted" value={marksSubmitted} />
        <MetricCard title="Places" value={call.awardQuota ?? "—"} />
      </div>
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === "applications" && <ApplicationsPanel call={call} rows={rows} isPending={rowsPending} committeeSize={members.length} onOpen={setOpenRow} />}
      {tab === "award" && <AwardListPanel call={call} />}
      <ScoredApplicationDialog key={openRow?.applicationId} call={call} row={openRow} onClose={() => setOpenRow(null)} />
    </Container>
  );
}
